/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import {
    type AfterViewInit, type OnDestroy, type OnInit, ChangeDetectionStrategy,
    ChangeDetectorRef, Component, ViewChild,
} from '@angular/core';
import { CoreEventServiceKeyE, EventService, I18nService, LayoutStyleService, LocalStorageService, PermissionService } from '@dm/core';
import { ActionType, BaseComponent, eventType, infigo, Path } from '@dm/live-material-infigo';
import { ToolService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';
import { type Subscription } from 'rxjs';

import { checkLicense } from '../conditionalSearch/grammar.util';
import { DEFAULT_TIP_MAP } from '../const';
import { formatTip, trim } from '../utils';
import * as Animations from './animations';

@Component({
    selector: 'app-infigo-input',
    templateUrl: './input.component.html',
    styleUrls: ['./input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [Animations.SlideDownMotion, Animations.FadeOutMotion],
})
export class InputComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('tipElement') tip;
    @ViewChild('errorDisplayComponent') errorElement;
    @ViewChild('inputElement', { static: true }) input;
    isMouseInInput = false;
    status: 'MONOPOLIZE_SEARCH' | 'SEARCH' | 'INIT'; // 当前状态
    select: number; // 当前选中的提示项
    inputTip; // 输入提示
    currentValue; // 当前输入值
    tipData = []; // 提示数据
    error: Record<string, unknown>; // 语法错误
    detach = false; // 是否将该组件从变更检测树上移出
    visible = false; // 控制小窗口搜索可见性
    placeholderVisible = true;
    collected: any; // 是否已收藏
    slideDown = false;
    collectable = false;
    DEFAULT_TIP: any[]; // 默认提示
    tipShow = true; // 是否展示提示
    preInput = ''; // 默认输入
    isBlankValue = false;
    cdkGlobalOverlayWrapper: HTMLElement;

    supportSmartThin = this.toolService.supportSmartThinLicense();
    placeholderText = this.supportSmartThin ?
        this.i18n.get('home.search_term_advanceSearch_info') :
        this.i18n.get('home.search_term_advanceSearch_D2100_info');

    /**
     * 事件服务中订阅的事件
     */
    events: Subscription[] = [];
    mapData = {
        currentValue: [Path.userInput, (value: string): string => {
            this.preInput = this.currentValue;
            if (value && document.activeElement !== this.input.nativeElement) {
                setTimeout(() => this.input.nativeElement.focus());
            }
            if (value.indexOf(' ') !== -1 && document.activeElement === this.input.nativeElement) {
                setTimeout(() => this.focus());
            }
            this.collected = infigo.collect.find(value, this.status === 'MONOPOLIZE_SEARCH');
            this.collectable = Boolean(value.trim());
            this.placeholderVisible = !value;
            return value;
        }],
        inputTip: [Path.inputTip, (value: { timeSelect: any }): { timeSelect: any } => {
            const lastChar = this.currentValue[this.currentValue.length - 1];

            if (value && value.timeSelect && ['>', '<', '='].includes(lastChar) && this.preInput.length < this.currentValue.length) {
                setTimeout(() => {
                    this.isMouseInInput = false;
                    this.input.nativeElement.blur();
                });
            }

            this.select = 0;

            if (this.tip) {
                this.tip.nativeElement.scrollTop = 0;
            }

            return value;
        }],
        tipData: [Path.tips, (value: any[]): any[] => {
            if (!this.currentValue) {
                return formatTip(this.DEFAULT_TIP, this.currentValue);
            }

            const unSupportTip = [];
            if (!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif) {
                unSupportTip.push(...[
                    'NVMe_over_RoCE', 'VMe_over_RoCE', 'Me_over_RoCE',
                    'e_over_RoCE', '_over_RoCE', 'over_RoCE',
                    'ver_RoCE', 'er_RoCE', 'r_RoCE', '_RoCE',
                    'RoCE', 'oCE', 'CE', 'E',
                ]);
            }

            if (!FeatureConfig.IsSupportFC) {
                unSupportTip.push(...[
                    'FC/FCoE', 'FCoE', 'C/FCoE',
                    '/FCoE',
                ]);
            }

            if (!FeatureConfig.IsSupportBGPLif) {
                unSupportTip.push(...['BGP', 'GP', 'P']);
            }

            return value.filter((item) => !unSupportTip.includes(item.emphasis));
        }],
        error: Path.error,
        status: Path.status,
    };

    constructor(public changeDetectorRef: ChangeDetectorRef,
        public i18n: I18nService,
        public local: LocalStorageService,
        public permissionService: PermissionService,
        public eventService: EventService,
        public toolService: ToolService,
        private layoutStyleService: LayoutStyleService,
    ) {
        super();
        const check = this.permissionService.isLicenseExist.bind(this.permissionService);
        this.DEFAULT_TIP = checkLicense(check, DEFAULT_TIP_MAP[i18n.language === 'ja' ? 'en' : i18n.language]);
    }

    ngOnInit(): void {
        super.ngOnInit();
        const tipShow = this.local.get('infigo-search-tip-show');
        this.tipShow = tipShow === '' ? true : tipShow;
        infigo.on(eventType.SEARCH_FINISHED, this.addHistory);
        window.addEventListener('keydown', this.keyBoard);
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit(() => {
            if (this.currentValue) {
                this.searchValue(this.currentValue);
            }
        });

        // 显示前调整top值
        setTimeout(() => {
            this.updateCdkGlobalOverlayWrapperPaddingTop();
        });
        // 显示过程中监听header上方提示栏的变化调整top值
        const checkTipsOverHeader$ = this.eventService.subscribe(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER, () => {
            this.updateCdkGlobalOverlayWrapperPaddingTop();
        });
        this.events.push(checkTipsOverHeader$);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        infigo.remove(eventType.SEARCH_FINISHED, this.addHistory);
        window.removeEventListener('keydown', this.keyBoard);

        // 当高级搜索关闭，还原cdkGlobalOverlayWrapper因为顶部提示栏适配的padding样式
        if (this.cdkGlobalOverlayWrapper) {
            this.cdkGlobalOverlayWrapper.style.paddingTop = '';
        }

        this.events.forEach((event) => {
            event.unsubscribe();
        });
    }

    /**
     * 更新高级搜索遮罩层的高度,优化与header栏的相对位置
     */
    updateCdkGlobalOverlayWrapperPaddingTop = (): void => {
        this.cdkGlobalOverlayWrapper = document.querySelector('.cdk-global-overlay-wrapper');
        if (this.cdkGlobalOverlayWrapper) {
            this.cdkGlobalOverlayWrapper.style.paddingTop = this.layoutStyleService.getHeightOfTipsOverHeader();
        }
    };

    addHistory = (value: any[]): void => {
        if (this.currentValue.length < 3) {
            return;
        }

        if (this.status === 'SEARCH') {
            if (value.every((v) => !v.result.length)) {
                return;
            }

            infigo.history.add(this.currentValue);
        } else {
            infigo.history.add(this.currentValue, true);
        }
    };

    keyBoard = (e: { stopImmediatePropagation?: any; preventDefault?: any; key?: any }): void => {
        const { key } = e;
        if (this.hasTips() && this.unpressFuncKey(e)) {
            if (['ArrowDown', 'ArrowUp'].includes(key) && this.visible) {
                e.stopImmediatePropagation();
                e.preventDefault();
                this.moveChoose(key === 'ArrowDown');
                return;
            }
            if (['Enter'].includes(key)) {
                const { tipData, select } = this;
                this.autoComplete(tipData[select]);
                this.select = 0;

                if (this.tip && this.tip.nativeElement) {
                    this.tip.nativeElement.scrollTop = 0;
                }
            }
        }
    };

    hasTips(): boolean {
        return this.tipData.length !== 0;
    }

    unpressFuncKey(e): boolean {
        const { metaKey, shiftKey, ctrlKey, altKey } = e;
        return !metaKey && !shiftKey && !ctrlKey && !altKey;
    }

    moveChoose(direction: boolean): void {
        if (direction) {
            this.select = this.select + 1 === this.tipData.length ? 0 : this.select + 1;
        } else {
            this.select = this.select === 0 ? this.tipData.length - 1 : this.select - 1;
        }
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
            const top = this.tip.nativeElement.scrollTop;
            const height = this.tip.nativeElement.clientHeight;
            const select = this.tip.nativeElement.querySelector('.select');
            const itemHeight = select.clientHeight;
            if (select.offsetTop - top < itemHeight || select.offsetTop - top + itemHeight > height) {
                select.scrollIntoView(select.offsetTop - top + itemHeight > height);
            }
        });
    }


    clearValue(): void {
        this.placeholderVisible = true;
        infigo.store.dispatch({
            type: ActionType.UserInput,
            value: '',
        });
    }

    focus(): void {
        this.slideDown = true;
        this.visible = true;
        if (!this.isInConditionSearch() && this.isEmptyTip()) {
            this.tipData = !this.currentValue ? this.DEFAULT_TIP : formatTip(this.DEFAULT_TIP, this.currentValue);
            this.select = 0;
            if (this.tip && this.tip.nativeElement) {
                this.tip.nativeElement.scrollTop = 0;
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    isInConditionSearch(): boolean {
        return this.currentValue.indexOf(' ') !== -1;
    }

    isEmptyTip(): boolean {
        return !this.tipData || this.tipData.length === 0;
    }

    enter(): void {
        this.isMouseInInput = true;
    }

    leave(): void {
        this.isMouseInInput = false;
        if (document.activeElement !== this.input.nativeElement) {
            this.blur();
        }
    }

    blur(): void {
        if (!this.isMouseInInput) {
            this.closeTip();
        }
    }

    closeTip(): void {
        this.slideDown = false;
        this.visible = false;
        this.changeDetectorRef.detectChanges();
    }

    autoComplete(value: { normal: any; emphasis: any }): void {
        infigo.store.dispatch({
            type: ActionType.UserInput,
            value: `${value.normal}${value.emphasis ? value.emphasis : ''}`,
        });
        this.input.nativeElement.focus();
    }

    searchValue(value: string): void {
        this.isBlankValue = /^\s+$/.test(value);
        infigo.store.dispatch({
            type: ActionType.UserInput,
            value: trim(value),
        });
    }

    onTimeSelectChange(time): void {
        this.searchValue(this.currentValue + time);
        this.input.nativeElement.focus();
    }

    shrink(): void {
        infigo.dispatch(eventType.SHRINK, this.currentValue);
    }

    collect(): void {
        if (!this.currentValue || !this.collectable || this.error) {
            return;
        }
        if (!this.collected) {
            this.collected = infigo.collect.add(this.currentValue);
        } else {
            infigo.collect.remove(this.collected.timestamp);
            this.collected = null;
        }
        this.changeDetectorRef.detectChanges();

        infigo.store.dispatch({
            type: ActionType.UpdateCollect,
            value: infigo.collect.getCollect(),
        });
    }

    scrollListen(e: { target: { scrollLeft: any } }): void {
        if (this.error) {
            this.errorElement.scroll(e.target.scrollLeft);
        }
    }

    showSpace(item: { normal: any; emphasis: any }): boolean {
        const str = item.normal + (item.emphasis || '');
        return str[str.length - 1] === ' ';
    }

    hidden(e: { target: { parentElement: { classList: { add: (arg0: string) => void } } } }): void {
        this.tipShow = false;
        this.local.set('infigo-search-tip-show', false);
        e.target.parentElement.classList.add('animate-hidden');
    }

    tipTrackBy(index: number, item: { [x: string]: string }): string {
        return Object.keys(item).reduce((pre, next) => pre + next + item[next], '');
    }
}
